/**
 * The one status chip (TMMAPP-0070, TMM-B2). The label is the catalog's `status.<domain>.<STATUS>`
 * — a status the registry does not know reads "Unknown status", never the raw code — and the tone
 * is `toneFor(domain, status)`, painted on guaranteed pairs (`toneColours`). The status is always
 * text (STATE-002, A11Y-005), so the colour never carries it alone. The label wraps instead of
 * clipping, so a long fr / pt wording fits at `xs`; chips in a row flex-wrap.
 */
import { Chip, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { resolveTokens } from '@/theme/theme';
import { toneFor, type StatusDomain } from '@/theme/statusTones';
import { toneChipSx } from '@/theme/toneChipSx';
import { statusLabel, type LabelValues } from './statusLabel';

interface StatusChipProps {
  domain: StatusDomain;
  status: string | null | undefined;
  size?: 'small' | 'medium';
  /** Interpolation values for a label that takes them (a connector's consent date). */
  values?: LabelValues | undefined;
}

export function StatusChip({ domain, status, size = 'small', values }: StatusChipProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const tone = toneFor(domain, status);
  return (
    <Chip
      component="span"
      size={size}
      variant={tone === 'draft' ? 'outlined' : 'filled'}
      label={statusLabel(t, domain, status, values)}
      data-tone={tone}
      sx={{
        ...toneChipSx(tokens, tone),
        maxWidth: '100%',
        height: 'auto',
        // The prototype's status pill (TMMAPP-0099, visual contract §9): radius 11, padding 3 9,
        // 11/500, not uppercase. A floor, not a fixed height: a long label wraps.
        minHeight: 22,
        borderRadius: '11px',
        '& .MuiChip-label': {
          display: 'block',
          whiteSpace: 'normal',
          overflowWrap: 'break-word',
          padding: '3px 9px',
          fontSize: 11,
          lineHeight: '16px',
          fontWeight: 500,
          letterSpacing: 0,
          textTransform: 'none',
        },
      }}
    />
  );
}
